/* ============================================================
   usePayload — structured "Send feedback" payload assembly
   ============================================================ */

import type { Tab, Comment } from '@/types'
import { AGENT_AUTHOR_ID, AGENT_AUTHOR_NAME } from '@/types'

function sortCommentsStable(comments: Comment[]): Comment[] {
  return comments
    .map((c, i) => ({ c, i }))
    .sort((a, b) => a.c.sourceLineStart - b.c.sourceLineStart || a.i - b.i)
    .map((x) => x.c)
}

function wrapLine(line: string, width: number): string[] {
  if (line.length <= width) return [line]
  const words = line.split(' ')
  const out: string[] = []
  let cur = ''
  for (const w of words) {
    if (cur === '') cur = w
    else if (cur.length + 1 + w.length <= width) cur += ' ' + w
    else {
      out.push(cur)
      cur = w
    }
  }
  if (cur !== '') out.push(cur)
  return out
}

function renderQuote(quotedText: string): string {
  let text = quotedText
  if (text.length > 400) text = text.slice(0, 400) + '...'
  const logical = text.split('\n')
  const wrapped: string[] = []
  for (const ln of logical) for (const part of wrapLine(ln, 78)) wrapped.push(part)
  return wrapped.map((l) => '> ' + l).join('\n')
}

function trimTrailing(s: string): string {
  return s.replace(/[\s﻿\xA0]+$/, '')
}

/** Thread status line for the header: RESOLVED (with who), or OPEN — flagged
 *  when the agent already replied and the thread is still/again open, so the
 *  agent knows this one bounced back rather than being fresh. */
function statusLabel(c: Comment): string {
  if (c.resolved) {
    const by = c.resolved.by === AGENT_AUTHOR_ID ? AGENT_AUTHOR_NAME : 'reviewer'
    return 'RESOLVED by ' + by
  }
  const agentReplied = (c.replies ?? []).some((r) => r.author.id === AGENT_AUTHOR_ID)
  return agentReplied ? 'OPEN (still open after your reply — take another look)' : 'OPEN'
}

/** One message line: `Name (id): note` — the id lets the agent reconstruct the
 *  author block verbatim when it carries the thread into the next revision.
 *  Agent messages are just `Agent:`. */
function messageLine(author: { id: string; name: string } | undefined, note: string): string {
  const text = trimTrailing(note)
  if (!author) return text
  if (author.id === AGENT_AUTHOR_ID) return AGENT_AUTHOR_NAME + ': ' + text
  return author.name + ' (' + author.id + '): ' + text
}

export function assemblePayload(tab: Tab, sourceUrl?: string): string | null {
  const hasSummary = (tab.summary || '').trim().length > 0
  const hasNotes = (tab.generalNotes || '').trim().length > 0
  // Drafts are uncommitted.
  const threads = (tab.comments || []).filter((c) => !c._draft)
  if (!hasSummary && !hasNotes && threads.length === 0) return null

  const parts: string[] = []
  // A self-describing preamble so the payload is actionable even pasted out of
  // context — it tells the agent this is review feedback and what to do with it.
  parts.push('Review feedback from redline.ink: revise the document, reply to each open')
  parts.push('thread with what changed, and carry every thread id into the new revision.')
  parts.push('Leave unsettled threads open and add anchored agent questions only where')
  parts.push('human judgment is still needed.')
  parts.push('')
  parts.push('Review of: ' + tab.filename + ' (' + tab.shortHash + '...)')
  if (sourceUrl) parts.push('Link: ' + sourceUrl)

  if (hasSummary) {
    parts.push('')
    parts.push('== Summary ==')
    parts.push(trimTrailing(tab.summary))
  }

  if (hasNotes) {
    parts.push('')
    parts.push('== General comments ==')
    parts.push(trimTrailing(tab.generalNotes))
  }

  if (threads.length > 0) {
    parts.push('')
    parts.push('== Comment threads ==')
    const sorted = sortCommentsStable(threads)
    sorted.forEach((c, idx) => {
      parts.push('')
      const n = idx + 1
      const hint = c.blockHint && c.blockHint.trim().length > 0 ? c.blockHint.trim() : ''
      const range =
        c.sourceLineStart === c.sourceLineEnd
          ? 'L' + c.sourceLineStart
          : 'L' + c.sourceLineStart + '-L' + c.sourceLineEnd
      const where = hint ? '§ "' + hint + '" (' + range + ')' : '(' + range + ')'
      parts.push('[' + n + '] ' + where + ' — ' + statusLabel(c) + ' — thread ' + c.id)
      parts.push(renderQuote(c.quotedText))
      if ((c.note || '').trim().length > 0) {
        parts.push('')
        parts.push(messageLine(c.author, c.note))
      }
      for (const r of c.replies ?? []) {
        parts.push('')
        parts.push(messageLine(r.author, r.note))
      }
    })
  }

  return trimTrailing(parts.join('\n'))
}
